import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';

const RegisterPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    contactType: 'email', // 'email' ou 'phone'
    password: '',
    confirmPassword: '',
    isFarmer: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Fonction pour réinitialiser le formulaire
  const resetForm = () => {
    setFormData({
      name: '',
      contact: '',
      contactType: 'email',
      password: '',
      confirmPassword: '',
      isFarmer: false
    });
  };
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleContactTypeChange = (type) => {
    setFormData({
      ...formData,
      contactType: type,
      contact: '' // Reset contact field when switching type
    });
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Le nom est requis');
      return false;
    }

    if (!formData.contact.trim()) {
      setError(`${formData.contactType === 'email' ? "L'email" : "Le numéro de téléphone"} est requis`);
      return false;
    }

    if (formData.contactType === 'email') {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(formData.contact)) {
        setError("Format d'email invalide");
        return false;
      }
    } else {
      // Format téléphone pour le Bénin: +22990000000
      //const phoneRegex = /^\+[0-9]{10,15}$/;
      // Accepte +22901XXXXXXXX (10 chiffres après +229) ou autre numéro international
      const phoneRegex = /^\+(22901\d{8}|[1-9]\d{6,14})$/;
      if (!phoneRegex.test(formData.contact)) {
        //setError("Format de téléphone invalide. Exemple: +22990000000");
        setError("Format de téléphone invalide. Exemple: +2290190000000");
        return false;
      }
    }
    if (formData.password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return false;
    }

    return true;
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  setError('');
  setSuccess('');

  if (!validateForm()) return;

  setLoading(true);

  try {
    // Séparer le nom en prénom et nom de famille
    const nameParts = formData.name.trim().split(' ');
    const prenom = nameParts[0] || 'User';
    const nom = nameParts.slice(1).join(' ') || 'User';

    // ✅ CORRECTION : Créer l'objet avec toutes les propriétés possibles
    const apiData: any = {
      name: formData.name,
      nom: nom,
      prenom: prenom,
      contact: formData.contact,
      password: formData.password,
      is_farmer: formData.isFarmer,
    };

    // Ajouter email ou phone selon le type
    if (formData.contactType === 'email') {
      apiData.email = formData.contact;
    } else {
      apiData.phone = formData.contact;
    }

    // Utiliser api.post directement (c'est axios)
    const response = await api.post('/auth/register', apiData);

    // Inscription réussie
    resetForm();
    setSuccess('Inscription réussie ! Vous allez être redirigé vers la page de connexion...');

    setTimeout(() => {
      navigate('/login', { 
        state: { message: 'Inscription réussie ! Vous pouvez maintenant vous connecter.' } 
      });
    }, 2000);

  } catch (err: any) {
    console.error('Erreur inscription:', err);
    const detail = err.response?.data?.detail;
    setError(Array.isArray(detail) ? detail.map((e: any) => e.msg || JSON.stringify(e)).join(", ") : (typeof detail === "string" ? detail : err.message || "Erreur lors de l'inscription"));
  } finally {
    setLoading(false);
  }
};

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold text-slate mb-6 text-center">Créer un compte</h1>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-slate font-medium mb-2" htmlFor="name">
              Nom complet
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-terracotta"
            />
          </div>



          {/* Sélection du type de contact */}
          <div className="mb-4">
            <label className="block text-slate font-medium mb-2">
              Comment souhaitez-vous vous connecter ?
            </label>
            <div className="flex border border-gray-300 rounded-md overflow-hidden">
              <button
                type="button"
                className={`flex-1 py-2 px-4 ${formData.contactType === 'email' ? 'bg-olive text-white' : 'bg-gray-100'}`}
                onClick={() => handleContactTypeChange('email')}
              >
                Email
              </button>
              <button
                type="button"
                className={`flex-1 py-2 px-4 ${formData.contactType === 'phone' ? 'bg-olive text-white' : 'bg-gray-100'}`}
                onClick={() => handleContactTypeChange('phone')}
              >
                Téléphone
              </button>
            </div>
          </div>

          {/* Champ de contact dynamique */}
          <div className="mb-4">
            <label className="block text-slate font-medium mb-2" htmlFor="contact">
              {formData.contactType === 'email' ? 'Email' : 'Téléphone (ex: +22990000000)'}
            </label>
            <input
              type={formData.contactType === 'email' ? 'email' : 'tel'}
              id="contact"
              name="contact"
              value={formData.contact}
              onChange={handleChange}
              placeholder={formData.contactType === 'email' ? 'votre@email.com' : '+22990000000'}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-terracotta"
            />
          </div>

          <div className="mb-4">
            <label className="block text-slate font-medium mb-2" htmlFor="password">
              Mot de passe
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-terracotta"
            />
          </div>


          <div className="mb-4">
            <label className="block text-slate font-medium mb-2" htmlFor="confirmPassword">
              Confirmer le mot de passe
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-terracotta"
            />
          </div>

          <div className="mb-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="isFarmer"
                checked={formData.isFarmer}
                onChange={handleChange}
                className="mr-2"
              />
              <span className="text-slate">Je suis un agriculteur/producteur</span>
            </label>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-olive text-white py-2 px-4 rounded-md hover:bg-terracotta transition duration-300 disabled:opacity-50"
          >
            {loading ? 'Inscription en cours...' : 'S\'inscrire'}
          </button>
        </form>

        <div className="mt-4 text-center">
          <p className="text-slate">
            Déjà un compte? <Link to="/login" className="text-terracotta hover:underline">Se connecter</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;


